import { useState } from 'react';
import { usePOS } from '@/contexts/POSContext';
import { useBCHPrice } from '@/hooks/useBCHPrice';
import { calcDiscount, calcTax, fiatToBCH, formatFiat, getCurrencySymbol } from '@/lib/bch';
import { cn } from '@/lib/utils';

const QUICK_PRESETS = [
  { label: '5% off',  type: 'percent' as const, value: 5 },
  { label: '10% off', type: 'percent' as const, value: 10 },
  { label: '15% off', type: 'percent' as const, value: 15 },
  { label: '20% off', type: 'percent' as const, value: 20 },
  { label: '25% off', type: 'percent' as const, value: 25 },
  { label: '50% off', type: 'percent' as const, value: 50 },
];

export function DiscountView() {
  const { settings, fiatAmount, setDiscount, setView, setTipAmount, setPendingBCH, coupons } = usePOS();
  const { data: priceData } = useBCHPrice(settings.currency);
  const sym = getCurrencySymbol(settings.currency);

  const [tab, setTab] = useState<'quick' | 'coupon'>('quick');

  // Quick discount state
  const [quickType, setQuickType] = useState<'percent' | 'flat'>('percent');
  const [quickValue, setQuickValue] = useState('');
  const [selectedPreset, setSelectedPreset] = useState<number | null>(null);

  // Coupon state
  const [couponInput, setCouponInput] = useState('');
  const [couponError, setCouponError] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<typeof coupons[0] | null>(null);

  // Compute discount amount for preview
  const quickDiscount = selectedPreset !== null
    ? calcDiscount(fiatAmount, 'percent', QUICK_PRESETS[selectedPreset].value)
    : quickValue
    ? calcDiscount(fiatAmount, quickType, parseFloat(quickValue) || 0)
    : 0;

  const couponDiscount = appliedCoupon
    ? calcDiscount(fiatAmount, appliedCoupon.type, appliedCoupon.value)
    : 0;

  const activeDiscount = tab === 'quick' ? quickDiscount : couponDiscount;
  const discountedTotal = Math.max(0, fiatAmount - activeDiscount);

  const handleApplyCoupon = () => {
    const code = couponInput.trim().toUpperCase();
    const found = coupons.find((c) => c.code.toUpperCase() === code && c.active);
    if (!found) {
      setCouponError('Code not found or inactive.');
      setAppliedCoupon(null);
      return;
    }
    setCouponError('');
    setAppliedCoupon(found);
  };

  const tippingEnabled = settings.enableTipping !== false;

  const proceedToPayment = (discount: number) => {
    // When skipping tip screen, calculate final BCH amount here
    setTipAmount(0);
    const discountedSubtotal = Math.max(0, fiatAmount - discount);
    const { total: grandTotal } = calcTax(discountedSubtotal, settings.taxRate);
    const bch = priceData ? fiatToBCH(grandTotal, priceData.price) : 0;
    setPendingBCH(bch);
    setView('payment');
  };

  const handleNext = () => {
    const discount = activeDiscount;
    if (discount > 0) {
      const label = tab === 'coupon' && appliedCoupon
        ? appliedCoupon.code
        : tab === 'quick' && selectedPreset !== null
        ? QUICK_PRESETS[selectedPreset].label
        : quickType === 'percent'
        ? `${quickValue}% off`
        : `${sym}${quickValue} off`;
      setDiscount(discount, label);
    } else {
      setDiscount(0, '');
    }
    if (tippingEnabled) {
      setView('tip');
    } else {
      proceedToPayment(discount);
    }
  };

  const handleSkip = () => {
    setDiscount(0, '');
    if (tippingEnabled) {
      setView('tip');
    } else {
      proceedToPayment(0);
    }
  };

  return (
    <div className="flex flex-col h-full gap-4">
      {/* Header */}
      <div className="text-center">
        <p className="text-sm font-medium text-zinc-400 uppercase tracking-wider mb-1">Apply Discount</p>
        <p className="text-lg font-bold text-zinc-800 dark:text-zinc-100">
          Order: {formatFiat(fiatAmount, settings.currency)}
        </p>
      </div>

      {/* Tab selector */}
      <div className="flex bg-zinc-100 dark:bg-zinc-800 rounded-2xl p-1">
        <button
          onClick={() => setTab('quick')}
          className={cn(
            'flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all',
            tab === 'quick'
              ? 'bg-white dark:bg-zinc-700 text-zinc-900 dark:text-zinc-50 shadow-sm'
              : 'text-zinc-400 dark:text-zinc-500'
          )}
        >
          🏷️ Quick Discount
        </button>
        <button
          onClick={() => setTab('coupon')}
          className={cn(
            'flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all',
            tab === 'coupon'
              ? 'bg-white dark:bg-zinc-700 text-zinc-900 dark:text-zinc-50 shadow-sm'
              : 'text-zinc-400 dark:text-zinc-500'
          )}
        >
          🎟️ Coupon Code
        </button>
      </div>

      {/* Quick discount tab */}
      {tab === 'quick' && (
        <div className="space-y-4">
          {/* Preset grid */}
          <div className="grid grid-cols-3 gap-2">
            {QUICK_PRESETS.map((preset, i) => (
              <button
                key={i}
                onClick={() => { setSelectedPreset(i); setQuickValue(''); }}
                className={cn(
                  'flex flex-col items-center py-3.5 rounded-2xl border-2 transition-all active:scale-95',
                  selectedPreset === i
                    ? 'bg-[#0AC18E] border-[#0AC18E] text-white shadow-lg shadow-[#0AC18E]/25'
                    : 'bg-white dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-200'
                )}
              >
                <span className="text-base font-black">{preset.label}</span>
                <span className={cn('text-xs mt-0.5', selectedPreset === i ? 'text-white/80' : 'text-zinc-400')}>
                  {sym}{calcDiscount(fiatAmount, 'percent', preset.value).toFixed(2)} off
                </span>
              </button>
            ))}
          </div>

          {/* Custom amount */}
          <div className="bg-white dark:bg-zinc-800 rounded-2xl border border-zinc-200 dark:border-zinc-700 p-4">
            <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-3">Custom Amount</p>
            {/* Type toggle */}
            <div className="flex gap-2 mb-3">
              {(['percent', 'flat'] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => { setQuickType(t); setSelectedPreset(null); }}
                  className={cn(
                    'flex-1 py-2 rounded-xl text-sm font-semibold border transition-all',
                    quickType === t && selectedPreset === null
                      ? 'bg-[#0AC18E] border-[#0AC18E] text-white'
                      : 'bg-zinc-50 dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700 text-zinc-500 dark:text-zinc-400'
                  )}
                >
                  {t === 'percent' ? '% Percent' : `${sym} Flat`}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 font-medium text-sm">
                  {quickType === 'percent' ? '%' : sym}
                </span>
                <input
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.01"
                  value={quickValue}
                  onChange={(e) => { setQuickValue(e.target.value); setSelectedPreset(null); }}
                  placeholder="0"
                  className="w-full pl-8 pr-4 py-3 bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-xl text-sm text-zinc-800 dark:text-zinc-200 focus:outline-none focus:ring-2 focus:ring-[#0AC18E]"
                />
              </div>
              {quickValue && parseFloat(quickValue) > 0 && (
                <span className="text-sm font-bold text-[#0AC18E] shrink-0">
                  −{sym}{calcDiscount(fiatAmount, quickType, parseFloat(quickValue)).toFixed(2)}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Coupon tab */}
      {tab === 'coupon' && (
        <div className="space-y-4">
          {coupons.filter((c) => c.active).length === 0 ? (
            <div className="text-center py-8 border border-dashed border-zinc-200 dark:border-zinc-700 rounded-2xl">
              <p className="text-3xl mb-2">🎟️</p>
              <p className="text-sm font-medium text-zinc-600 dark:text-zinc-300 mb-1">No coupons set up yet</p>
              <p className="text-xs text-zinc-400 mb-3">Go to Settings to create coupon codes</p>
              <button
                onClick={() => setView('settings')}
                className="text-xs font-bold text-[#0AC18E] hover:underline"
              >
                Go to Settings →
              </button>
            </div>
          ) : (
            <>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={couponInput}
                  onChange={(e) => { setCouponInput(e.target.value.toUpperCase()); setCouponError(''); setAppliedCoupon(null); }}
                  placeholder="Enter coupon code"
                  className="flex-1 bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl px-4 py-3 text-sm font-mono text-zinc-800 dark:text-zinc-200 uppercase placeholder:normal-case placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#0AC18E]"
                />
                <button
                  onClick={handleApplyCoupon}
                  className="px-4 py-3 rounded-xl bg-[#0AC18E] text-white text-sm font-bold shadow-sm hover:bg-[#09AF81] active:scale-95 transition-all"
                >
                  Apply
                </button>
              </div>
              {couponError && <p className="text-sm text-red-400 font-medium">{couponError}</p>}
              {appliedCoupon && (
                <div className="bg-[#0AC18E]/10 dark:bg-[#0AC18E]/20 border border-[#0AC18E]/30 rounded-2xl p-4 flex items-center gap-3">
                  <span className="text-2xl">✅</span>
                  <div>
                    <p className="text-sm font-bold text-zinc-800 dark:text-zinc-100">
                      {appliedCoupon.code} applied!
                    </p>
                    <p className="text-xs text-zinc-500 dark:text-zinc-400">
                      {appliedCoupon.type === 'percent' ? `${appliedCoupon.value}% off` : `${sym}${appliedCoupon.value} off`}
                      {appliedCoupon.description ? ` · ${appliedCoupon.description}` : ''}
                      {' '}→ saving {sym}{couponDiscount.toFixed(2)}
                    </p>
                  </div>
                </div>
              )}

              {/* Available coupons list */}
              <div>
                <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">Available Codes</p>
                <div className="space-y-2">
                  {coupons.filter((c) => c.active).map((c) => (
                    <button
                      key={c.id}
                      onClick={() => { setCouponInput(c.code); setCouponError(''); setAppliedCoupon(c); }}
                      className={cn(
                        'w-full flex items-center justify-between px-4 py-3 rounded-xl border transition-all text-left',
                        appliedCoupon?.id === c.id
                          ? 'bg-[#0AC18E] border-[#0AC18E] text-white'
                          : 'bg-white dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 hover:border-[#0AC18E]/40'
                      )}
                    >
                      <div>
                        <p className={cn('text-sm font-bold font-mono', appliedCoupon?.id === c.id ? 'text-white' : 'text-zinc-800 dark:text-zinc-100')}>{c.code}</p>
                        {c.description && <p className={cn('text-xs', appliedCoupon?.id === c.id ? 'text-white/80' : 'text-zinc-400')}>{c.description}</p>}
                      </div>
                      <span className={cn('text-sm font-bold', appliedCoupon?.id === c.id ? 'text-white' : 'text-[#0AC18E]')}>
                        {c.type === 'percent' ? `${c.value}%` : `${sym}${c.value}`} off
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Summary */}
      {activeDiscount > 0 && (
        <div className="bg-white dark:bg-zinc-800 rounded-2xl border border-zinc-100 dark:border-zinc-700 p-4 flex items-center justify-between">
          <div>
            <p className="text-xs text-zinc-400 uppercase tracking-wider mb-0.5">After Discount</p>
            <p className="text-2xl font-black text-zinc-900 dark:text-zinc-50">
              {formatFiat(discountedTotal, settings.currency)}
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs text-zinc-400 mb-0.5">Saving</p>
            <p className="text-lg font-bold text-[#0AC18E]">−{formatFiat(activeDiscount, settings.currency)}</p>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-col gap-2 mt-auto">
        <button
          onClick={handleNext}
          className="w-full py-4 rounded-2xl text-base font-bold bg-[#0AC18E] text-white shadow-lg shadow-[#0AC18E]/30 hover:bg-[#09AF81] active:scale-98 transition-all"
        >
          {activeDiscount > 0
            ? `Apply & Continue (save ${formatFiat(activeDiscount, settings.currency)})`
            : 'Continue without discount'}
        </button>
        <button
          onClick={handleSkip}
          className="w-full py-3 rounded-2xl text-sm font-semibold bg-zinc-100 dark:bg-zinc-800 text-zinc-500 border border-zinc-200 dark:border-zinc-700"
        >
          ← Back to Checkout
        </button>
      </div>
    </div>
  );
}
